/**
 * CR-1154: Hyperlink to a Purchase Order header ("Sales Order header" in
 * supplier mode).
 *
 * The PO header detail is a modal on the Purchasing page's PO Headers tab,
 * deep-linkable as `/purchasing?tab=po_headers&po=<poNumber>` (parsed in
 * Purchasing.tsx). Where a row shows both a PO number and a line number, the PO
 * number links here (the header) and the line number links to the PO line item
 * detail via {@link PoLineLink}.
 *
 * Opens in a new tab, consistent with PoLineLink / GrnLink. Degrades to plain
 * text when there is no PO number.
 *
 * @param poNumber - The PO number. Empty/null renders children as plain text.
 * @param children - The label to render (defaults to the PO number).
 * @param className - Optional override for the link styling.
 * @returns A router link to the PO header, or the bare children.
 *
 * @example
 * <PoHeaderLink poNumber={grn.po_number} />
 */
import { Link } from 'react-router-dom';
import { ReactNode } from 'react';

interface PoHeaderLinkProps {
  poNumber?: string | null;
  children?: ReactNode;
  className?: string;
}

/** Canonical deep-link for a PO header on the PO Headers tab. */
export function poHeaderHref(poNumber: string): string {
  return `/purchasing?tab=po_headers&po=${encodeURIComponent(poNumber)}`;
}

export default function PoHeaderLink({
  poNumber,
  children,
  className = 'text-primary hover:underline',
}: PoHeaderLinkProps) {
  const label = children ?? poNumber ?? '-';
  if (!poNumber) {
    return <>{label}</>;
  }
  return (
    <Link to={poHeaderHref(poNumber)} target="_blank" rel="noopener noreferrer" className={className}>
      {label}
    </Link>
  );
}
